import "./sentry";
import * as Sentry from "@sentry/react";
import { StrictMode, useMemo } from "react";
import { createRoot, type Root } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { createTranslator } from "@heylife/i18n";
import { detectLocale, I18nContext } from "./i18n";
import { router } from "./router";
import { SentryErrorFallback } from "./components/SentryErrorFallback";
import "./styles.css";

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

function App() {
  const locale = detectLocale();
  const value = useMemo(() => ({ locale, t: createTranslator(locale).t }), [locale]);
  document.documentElement.lang = locale;
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <SentryErrorFallback resetError={resetError} />}>
      <I18nContext.Provider value={value}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </I18nContext.Provider>
    </Sentry.ErrorBoundary>
  );
}

const container = document.getElementById("root")!;
const rootKey = "__HEYLIFE_ADMIN_ROOT__";
const rootOptions = {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
} as const;

const globals = globalThis as typeof globalThis & { [rootKey]?: Root };
const root = globals[rootKey] ?? createRoot(container, rootOptions);
if (!globals[rootKey]) globals[rootKey] = root;

root.render(
  <StrictMode>
    <App />
  </StrictMode>,
);
